/** Form controls: visible labels, hint under the label, error under the field. 16 px text (no iOS zoom). */
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { forwardRef, useEffect, useId, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from './core';

export function Label({ htmlFor, children, hint, optional }: { htmlFor?: string; children: ReactNode; hint?: ReactNode; optional?: boolean }) {
  return (
    <div className="mb-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-ink">
        {children} {optional && <span className="font-normal text-muted">(optional)</span>}
      </label>
      {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}
    </div>
  );
}

export const inputCls =
  'block w-full min-h-touch rounded-tag border border-strong bg-card px-3 text-base text-ink placeholder:text-muted/80 outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-sunken disabled:opacity-70';

interface FieldBits {
  label: string;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  /** Hide the label visually (still read by screen readers). */
  srLabel?: boolean;
}

function FieldShell({ id, label, hint, error, optional, srLabel, className, children }: FieldBits & { id: string; className?: string; children: ReactNode }) {
  return (
    <div className={className}>
      {srLabel ? (
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
      ) : (
        <Label htmlFor={id} hint={hint} optional={optional}>
          {label}
        </Label>
      )}
      {children}
      {error && (
        <p id={`${id}-e`} className="mt-1 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldBits & { /** Unit shown inside the field on the left, e.g. ₱. */ prefix?: string }>(function Input(
  { label, hint, error, optional, srLabel, className, prefix, ...input },
  ref,
) {
  const id = useId();
  const field = <input ref={ref} id={id} aria-invalid={!!error || undefined} aria-describedby={error ? `${id}-e` : undefined} className={cx(inputCls, error && 'border-danger', prefix && 'pl-8')} {...input} />;
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} optional={optional} srLabel={srLabel} className={className}>
      {prefix ? (
        <div className="relative">
          <span aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
            {prefix}
          </span>
          {field}
        </div>
      ) : (
        field
      )}
    </FieldShell>
  );
});

export function TextArea({ label, hint, error, optional, srLabel, className, ...input }: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldBits) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} optional={optional} srLabel={srLabel} className={className}>
      <textarea id={id} rows={4} aria-invalid={!!error || undefined} className={cx(inputCls, 'py-2.5 leading-relaxed', error && 'border-danger')} {...input} />
    </FieldShell>
  );
}

export function Select({ label, hint, error, optional, srLabel, className, children, ...input }: SelectHTMLAttributes<HTMLSelectElement> & FieldBits) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} optional={optional} srLabel={srLabel} className={className}>
      <div className="relative">
        <select id={id} className={cx(inputCls, 'appearance-none pr-9', error && 'border-danger')} {...input}>
          {children}
        </select>
        <ChevronDown aria-hidden className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
      </div>
    </FieldShell>
  );
}

/** Search box with clear button; used in the table filter bar and the top bar. */
export const SearchInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & { value: string; onChange: (v: string) => void; label: string }>(
  function SearchInput({ value, onChange, label, className, ...rest }, ref) {
    return (
      <div className={cx('relative', className)}>
        <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <input
          ref={ref}
          type="search"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cx(inputCls, 'pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden')}
          {...rest}
        />
        {value && (
          <button type="button" aria-label="Clear search" onClick={() => onChange('')} className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-tag text-muted hover:text-ink">
            <X aria-hidden className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  },
);

export interface ComboOption {
  value: string;
  label: string;
  sub?: string;
}

/**
 * Type-to-filter picker (ARIA combobox + listbox). For long lists — customers, items,
 * categories. Pass `onQuery` to search the server instead of filtering locally.
 */
export function Combobox({
  label,
  options,
  value,
  onChange,
  placeholder = 'Type to search',
  hint,
  error,
  optional,
  onQuery,
  loading,
  className,
}: FieldBits & {
  options: ComboOption[];
  value: string | null;
  onChange: (v: string | null, o: ComboOption | null) => void;
  placeholder?: string;
  onQuery?: (q: string) => void;
  loading?: boolean;
  className?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;
  const shown = useMemo(() => {
    if (onQuery || !q) return options.slice(0, 100);
    const n = q.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(n) || o.sub?.toLowerCase().includes(n)).slice(0, 100);
  }, [options, q, onQuery]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pick = (o: ComboOption | null) => {
    onChange(o?.value ?? null, o);
    setQ('');
    setOpen(false);
  };

  return (
    <FieldShell id={id} label={label} hint={hint} error={error} optional={optional} className={className}>
      <div ref={wrap} className="relative">
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open && shown[active] ? `${id}-o-${active}` : undefined}
          className={cx(inputCls, 'pr-9', error && 'border-danger')}
          placeholder={selected ? selected.label : placeholder}
          value={open ? q : selected?.label ?? ''}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
            setOpen(true);
            onQuery?.(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(a + 1, shown.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && open && shown[active]) {
              e.preventDefault();
              pick(shown[active]);
            } else if (e.key === 'Escape') setOpen(false);
          }}
        />
        {selected && !open ? (
          <button type="button" aria-label={`Clear ${label}`} onClick={() => pick(null)} className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center text-muted hover:text-ink">
            <X aria-hidden className="h-4 w-4" />
          </button>
        ) : (
          <ChevronDown aria-hidden className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        )}
        {open && (
          <ul id={`${id}-list`} role="listbox" className="absolute z-40 mt-1 max-h-72 w-full overflow-auto rounded-tag border border-line bg-card py-1 shadow-lift">
            {loading && <li className="px-3 py-2 text-sm text-muted">Searching…</li>}
            {!loading && shown.length === 0 && <li className="px-3 py-2 text-sm text-muted">No matches</li>}
            {shown.map((o, i) => (
              <li
                key={o.value}
                id={`${id}-o-${i}`}
                role="option"
                aria-selected={o.value === value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
                onMouseEnter={() => setActive(i)}
                className={cx('flex min-h-touch cursor-pointer items-center gap-2 px-3 py-1.5', i === active && 'bg-sunken')}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{o.label}</span>
                  {o.sub && <span className="block truncate text-xs text-muted">{o.sub}</span>}
                </span>
                {o.value === value && <Check aria-hidden className="h-4 w-4 text-brand" />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </FieldShell>
  );
}

export function Checkbox({ label, checked, onChange, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; hint?: ReactNode }) {
  return (
    <label className="flex min-h-touch cursor-pointer items-start gap-3 py-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 rounded border-strong accent-[rgb(var(--brand))]" />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-muted">{hint}</span>}
      </span>
    </label>
  );
}

export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label className="flex min-h-touch cursor-pointer items-center gap-3">
      <span className="flex-1">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-muted">{hint}</span>}
      </span>
      <span className="relative">
        <input type="checkbox" role="switch" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="block h-7 w-12 rounded-full bg-strong transition-colors peer-checked:bg-brand peer-focus-visible:ring-2 peer-focus-visible:ring-brand" />
        <span className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

/** One-of-few choice shown as a segmented control. */
export function Segmented<V extends string>({ value, options, onChange, label, className }: { value: V; options: { value: V; label: string }[]; onChange: (v: V) => void; label: string; className?: string }) {
  return (
    <div role="radiogroup" aria-label={label} className={cx('inline-flex gap-1 rounded-tag border border-line bg-sunken p-1', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          role="radio"
          type="button"
          aria-checked={o.value === value}
          onClick={() => onChange(o.value)}
          className={cx('min-h-9 whitespace-nowrap rounded-[4px] px-3 text-sm font-semibold transition-colors', o.value === value ? 'bg-card text-ink shadow-panel' : 'text-muted hover:text-ink')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
